// WINHMS_RECON_V2 — WinHMS POS Import modal (three .xls slots → tiered
// reconciliation gate → prefill apply).
//
// Opens from a "Import from WinHMS" button on the Daily Entry page. The
// modal NEVER pre-fills silently. Preview shows every per-check status
// under the server-controlled WINHMS_RECON_V2 policy:
//   RECONCILED               — |diff| ≤ ₹1
//   ACCEPTED_WITH_VARIANCE   — variance_level = minor  (soft band)
//   ACCEPTED_WITH_VARIANCE   — variance_level = review (loose band)
//   BLOCKED                  — |diff| exceeds the hard band
// Overall reconciliation_status = worst of the per-check statuses.
// Both minor and review variances may proceed to Apply; only BLOCKED
// blocks it. Tolerance policy is server-controlled — NOTHING is sent
// from the client.
//
// Apply hits POST /api/pos-import/apply which:
//   1) re-runs the server-side reconciliation gate (identical policy)
//   2) upserts liquor_sale + settlement_breakdown + pos_import_meta on
//      the daily-entry doc (persists variance metadata for audit)
// Manager still has to submit the sales_data section through the
// normal flow.

import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Upload, CheckCircle2, XCircle, FileSpreadsheet, AlertTriangle, Loader2 } from 'lucide-react';

// ITER249 — Display rounding: whole numbers everywhere. The reconciler
// still operates on stored 2dp values; this only changes how the preview
// summary numbers render to the user.
const fmt = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;

function FileSlot({ label, hint, file, onPick, testId }) {
  return (
    <div className="border border-dashed border-[#E2E8DF] rounded-md p-3 hover:bg-[#F7F6F1]/50 transition-colors">
      <div className="flex items-start gap-3">
        <FileSpreadsheet className="w-5 h-5 mt-0.5 text-[#5C6B64] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-[#1A2421]">{label}</div>
          <div className="text-[11px] text-[#5C6B64] mt-0.5">{hint}</div>
          {file && (
            <div className="mt-1.5 text-[11px] text-[#2D6A4F] font-medium truncate" data-testid={`${testId}-name`}>
              ✓ {file.name}
            </div>
          )}
        </div>
        <label className="text-[11px] text-[color:var(--color-primary)] font-medium cursor-pointer hover:underline whitespace-nowrap">
          {file ? 'Replace' : 'Browse'}
          <input
            type="file"
            accept=".xls,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] || null)}
            data-testid={`${testId}-input`}
          />
        </label>
      </div>
    </div>
  );
}

// Per-check status chip. Uses the server's status verbatim — no client
// re-derivation of tolerances.
const STATUS_STYLE = {
  RECONCILED:              { bg: '#DBF3E4', fg: '#1F6A45', label: 'Reconciled' },
  ACCEPTED_WITH_VARIANCE:  { bg: '#FFF4D6', fg: '#875F14', label: 'Variance' },
  BLOCKED:                 { bg: '#FEE5DC', fg: '#C45A41', label: 'Blocked' },
};
function StatusChip({ status, variance_level, small }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.BLOCKED;
  let label = st.label;
  if (status === 'ACCEPTED_WITH_VARIANCE') {
    label = variance_level === 'review' ? 'Review Variance' : 'Minor Variance';
  }
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium tabular-nums ${small ? 'text-[10px] px-1.5 py-0.5' : 'text-[11px] px-2 py-0.5'}`}
      style={{ backgroundColor: st.bg, color: st.fg }}
      data-testid={`recon-status-chip-${status}${variance_level ? '-' + variance_level : ''}`}
    >
      {label}
    </span>
  );
}

function CheckRow({ check }) {
  const status = check.status || (check.ok ? 'RECONCILED' : 'BLOCKED');
  return (
    <div className="flex items-start gap-2 text-[11px] py-1" data-testid="recon-check-row">
      <StatusChip status={status} variance_level={check.variance_level} small />
      <span className="text-[#1A2421] flex-1">{check.label}</span>
      {(check.lhs != null || check.rhs != null) && (
        <span className="tabular-nums text-[#5C6B64]">
          {fmt(check.lhs)} vs {fmt(check.rhs)} · Δ {fmt(check.diff)}
          {check.threshold_used != null && (
            <span className="text-[#8A9591] ml-1">
              (limit {fmt(check.threshold_used)})
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export default function PosImportModal({ open, onClose, outletId, onApplied }) {
  const { API, getHeaders } = useAuth();
  const [salesFile, setSalesFile]         = useState(null);
  const [nightFile, setNightFile]         = useState(null);
  const [occupancyFile, setOccupancyFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying]     = useState(false);
  const [preview, setPreview]       = useState(null);   // server response

  const allPicked = salesFile && nightFile && occupancyFile;

  const reset = () => {
    setSalesFile(null); setNightFile(null); setOccupancyFile(null);
    setPreview(null); setPreviewing(false); setApplying(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const submit = async (mode) => {
    if (!allPicked) {
      toast.error('Please attach all three files');
      return;
    }
    const isApply = mode === 'apply';
    if (isApply) setApplying(true); else setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('outlet_id', outletId);
      fd.append('sales_file', salesFile);
      fd.append('night_file', nightFile);
      fd.append('occupancy_file', occupancyFile);
      const url = isApply ? `${API}/pos-import/apply` : `${API}/pos-import/preview`;
      const res = await axios.post(url, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (isApply) {
        toast.success('Imported & pre-filled');
        onApplied?.(res.data);
        handleClose();
      } else {
        setPreview(res.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.reason === 'reconciliation_failed') {
        // Server-side reconciliation failure on apply — surface all fields
        setPreview({
          ok: false,
          policy_version:        detail.policy_version,
          reconciliation_status: detail.reconciliation_status || 'BLOCKED',
          variance_level:        detail.variance_level,
          blocking_errors: detail.blocking_errors || [],
          checks: detail.checks || [],
          warnings: detail.warnings || [],
          per_file: detail.per_file || [],
          prefill: null,
        });
        toast.error('Reconciliation failed — see details below');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Import failed');
      }
    } finally {
      setPreviewing(false); setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pos-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
            <Upload className="w-5 h-5 text-[color:var(--color-primary)]" />
            Import from WinHMS
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#5C6B64]">
            Attach today&apos;s three WinHMS exports. Nothing is pre-filled until every reconciliation check passes to the rupee.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          <FileSlot label="Sales Day Book (POS)" hint="WinHMS bill-level export — file title starts 'Sales Day Book'"
            file={salesFile} onPick={setSalesFile} testId="pos-import-sales" />
          <FileSlot label="Night Audit Report" hint="WinHMS revenue & taxes — file title starts 'Night Audit Report'"
            file={nightFile} onPick={setNightFile} testId="pos-import-night" />
          <FileSlot label="Occupancy Report" hint="WinHMS rooms — file title starts 'Occupancy Analysis'"
            file={occupancyFile} onPick={setOccupancyFile} testId="pos-import-occupancy" />
        </div>

        {preview && (
          <div className="mt-3 border border-[#E2E8DF] rounded-md p-3 bg-[#F7F6F1]/40" data-testid="pos-import-preview">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {preview.reconciliation_status === 'BLOCKED' ? (
                <XCircle className="w-4 h-4 text-[#C45A41]" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" />
              )}
              <span className="text-[13px] font-medium text-[#1A2421]">
                {preview.reconciliation_status === 'BLOCKED'
                  ? 'Reconciliation blocked'
                  : preview.reconciliation_status === 'ACCEPTED_WITH_VARIANCE'
                    ? `Accepted with variance — date ${preview.date}`
                    : `Reconciled — date ${preview.date}`}
              </span>
              {preview.reconciliation_status && (
                <StatusChip
                  status={preview.reconciliation_status}
                  variance_level={preview.variance_level}
                />
              )}
              {preview.policy_version && (
                <span
                  className="text-[10px] text-[#5C6B64] tabular-nums font-mono"
                  data-testid="recon-policy-version"
                >
                  {preview.policy_version}
                </span>
              )}
            </div>

            {/* File-by-file identification result */}
            {preview.per_file?.length > 0 && (
              <div className="text-[11px] space-y-0.5 mb-2">
                {preview.per_file.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    {f.ok ? <CheckCircle2 className="w-3 h-3 text-[#2D6A4F]" />
                          : <XCircle className="w-3 h-3 text-[#C45A41]" />}
                    <span className="text-[#5C6B64]">{f.filename}</span>
                    {f.ok && <span className="text-[#1A2421] font-medium">→ {f.kind} ({f.date})</span>}
                    {!f.ok && <span className="text-[#C45A41]">{f.error}</span>}
                  </div>
                ))}
              </div>
            )}

            {preview.blocking_errors?.length > 0 && (
              <div className="mt-2 p-2 bg-[#FEE5DC] border border-[#C45A41]/30 rounded text-[12px]" data-testid="pos-import-blocking">
                <div className="font-medium text-[#C45A41] mb-1">Must fix before pre-fill:</div>
                <ul className="list-disc list-inside space-y-0.5 text-[#1A2421]">
                  {preview.blocking_errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {preview.warnings?.length > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded text-[12px]">
                <div className="font-medium text-amber-700 mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Warnings (not blocking):
                </div>
                <ul className="list-disc list-inside text-[#1A2421]">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {preview.checks?.length > 0 && (
              <details className="mt-2 text-[11px]" data-testid="pos-import-checks-toggle">
                <summary className="cursor-pointer text-[color:var(--color-primary)] font-medium select-none">
                  Show all {preview.checks.length} reconciliation checks
                </summary>
                <div className="mt-2 border-t border-[#E2E8DF] pt-2">
                  {preview.checks.map((c, i) => <CheckRow key={i} check={c} />)}
                </div>
              </details>
            )}

            {preview.ok && preview.prefill && (
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]" data-testid="pos-import-prefill-preview">
                <div className="text-[#5C6B64]">Food Sale</div>
                <div className="text-right tabular-nums font-medium">{fmt(preview.prefill.food_sale)}</div>
                <div className="text-[#5C6B64]">Beverage Sale (incl. Liquor)</div>
                <div className="text-right tabular-nums font-medium">{fmt(preview.prefill.beverage_sale)}</div>
                <div className="text-[#5C6B64]">Liquor (analytics-only)</div>
                <div className="text-right tabular-nums">{fmt(preview.prefill.liquor_sale)}</div>
                <div className="text-[#5C6B64]">Net Sales (F&B)</div>
                <div className="text-right tabular-nums font-semibold">{fmt(preview.prefill.gross_sales)}</div>
                <div className="text-[#5C6B64] col-span-2 pt-1.5 border-t border-[#E2E8DF] mt-1.5 text-[10px] uppercase tracking-wider">Daypart</div>
                <div className="text-[#5C6B64]">Lunch (incl. Plan)</div>
                <div className="text-right tabular-nums">
                  {fmt(preview.prefill.lunch_sales)} · {preview.prefill.lunch_covers} covers
                </div>
                <div className="text-[#5C6B64]">Dinner</div>
                <div className="text-right tabular-nums">
                  {fmt(preview.prefill.dinner_sales)} · {preview.prefill.dinner_covers} covers
                </div>
                <div className="text-[#5C6B64] col-span-2 pt-1.5 border-t border-[#E2E8DF] mt-1.5 text-[10px] uppercase tracking-wider">Rooms</div>
                <div className="text-[#5C6B64]">Covers (Total)</div>
                <div className="text-right tabular-nums">{preview.prefill.number_of_covers}</div>
                <div className="text-[#5C6B64]">Room Revenue</div>
                <div className="text-right tabular-nums">{fmt(preview.prefill.room_revenue)}</div>
                <div className="text-[#5C6B64]">Rooms Sold / Available</div>
                <div className="text-right tabular-nums">{preview.prefill.rooms_sold} / {preview.prefill.rooms_available}</div>
                <div className="text-[#5C6B64]">ARR · RevPar · Pax</div>
                <div className="text-right tabular-nums">
                  {fmt(preview.prefill.arr)} · {fmt(preview.prefill.revpar)} · {preview.prefill.room_pax}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={handleClose} data-testid="pos-import-cancel">Cancel</Button>
          <Button
            variant="outline"
            onClick={() => submit('preview')}
            disabled={!allPicked || previewing}
            data-testid="pos-import-preview-btn"
          >
            {previewing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            Preview
          </Button>
          <Button
            onClick={() => submit('apply')}
            disabled={!allPicked || applying || (preview && preview.reconciliation_status === 'BLOCKED')}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="pos-import-apply-btn"
          >
            {applying && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            Apply to Daily Entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
